import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const PhotoUpload = ({ photos, onChange, maxPhotos = 10 }) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);

  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  const maxFileSize = 5 * 1024 * 1024; // 5MB

  const validateFile = (file) => {
    if (!allowedTypes?.includes(file?.type)) {
      return 'Only JPG, PNG, and WebP images are allowed';
    }
    if (file?.size > maxFileSize) {
      return 'File size must be less than 5MB';
    }
    return null;
  };

  const handleFiles = (files) => {
    setUploadError('');
    const fileArray = Array.from(files);
    
    if (photos?.length + fileArray?.length > maxPhotos) {
      setUploadError(`Maximum ${maxPhotos} photos allowed`);
      return;
    }

    const validFiles = [];
    const errors = [];

    fileArray?.forEach(file => {
      const error = validateFile(file);
      if (error) {
        errors?.push(`${file?.name}: ${error}`);
      } else {
        const reader = new FileReader();
        reader.onload = (e) => {
          validFiles?.push({
            id: Date.now() + Math.random(),
            file: file,
            preview: e?.target?.result,
            name: file?.name,
            size: file?.size
          });

          if (validFiles?.length === fileArray?.length - errors?.length) {
            onChange([...photos, ...validFiles]);
          }
        };
        reader?.readAsDataURL(file);
      }
    });

    if (errors?.length > 0) {
      setUploadError(errors?.join('; '));
    }
  };

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === 'dragenter' || e?.type === 'dragover') {
      setDragActive(true);
    } else if (e?.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);

    if (e?.dataTransfer?.files && e?.dataTransfer?.files?.length > 0) {
      handleFiles(e?.dataTransfer?.files);
    }
  };

  const handleFileInput = (e) => {
    if (e?.target?.files && e?.target?.files?.length > 0) {
      handleFiles(e?.target?.files);
    }
  };

  const removePhoto = (photoId) => {
    onChange(photos?.filter(photo => photo?.id !== photoId));
    setUploadError('');
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes?.[i];
  };

  return (
    <div className="bg-card rounded-lg p-4 md:p-6 lg:p-8 shadow-elevation-md">
      <div className="flex items-center gap-3 mb-4 md:mb-6">
        <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-accent/10 flex items-center justify-center">
          <Icon name="Image" size={20} className="text-accent" />
        </div>
        <div>
          <h3 className="text-lg md:text-xl font-semibold text-foreground">Property Photos</h3>
          <p className="text-sm text-muted-foreground">Upload up to {maxPhotos} photos (JPG, PNG, WebP)</p>
        </div>
      </div>
      <div
        className={`relative border-2 border-dashed rounded-lg transition-all duration-250 ${
          dragActive 
            ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 bg-muted/20'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/jpeg,image/jpg,image/png,image/webp"
          onChange={handleFileInput}
          className="hidden"
        />

        <div className="p-6 md:p-8 lg:p-12 text-center">
          <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
            <Icon name="Upload" size={32} className="text-primary" />
          </div>
          
          <h4 className="text-base md:text-lg font-semibold text-foreground mb-2">
            Drop photos here or click to browse
          </h4>
          <p className="text-sm text-muted-foreground mb-4 md:mb-6">
            Maximum file size: 5MB per photo
          </p>

          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef?.current?.click()}
            iconName="FolderOpen"
            iconPosition="left"
          >
            Select Photos
          </Button>
        </div>
      </div>
      {uploadError && (
        <div className="mt-4 flex items-start gap-2 p-3 bg-destructive/10 rounded-lg">
          <Icon name="AlertCircle" size={16} className="text-destructive flex-shrink-0 mt-0.5" />
          <p className="text-sm text-destructive">{uploadError}</p>
        </div>
      )}
      {photos?.length > 0 && (
        <div className="mt-6 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-foreground">
              Uploaded Photos ({photos?.length}/{maxPhotos})
            </p>
            {photos?.length > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onChange([])}
                iconName="Trash2"
                iconPosition="left"
              >
                Remove All
              </Button>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {photos?.map((photo) => (
              <div key={photo?.id} className="relative group">
                <div className="aspect-square rounded-lg overflow-hidden bg-muted">
                  <Image
                    src={photo?.preview}
                    alt={`Property photo ${photo?.name}`}
                    className="w-full h-full object-cover"
                  />
                </div>
                
                <button
                  type="button"
                  onClick={() => removePhoto(photo?.id)}
                  className="absolute top-2 right-2 w-8 h-8 rounded-full bg-destructive text-destructive-foreground opacity-0 group-hover:opacity-100 transition-opacity duration-250 flex items-center justify-center shadow-elevation-md"
                  aria-label={`Remove ${photo?.name}`}
                >
                  <Icon name="X" size={16} />
                </button>

                <div className="mt-2">
                  <p className="text-xs text-foreground truncate">{photo?.name}</p>
                  <p className="text-xs text-muted-foreground">{formatFileSize(photo?.size)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoUpload;